import find from 'lodash/find';
import { filter, map, orderBy } from 'lodash';
import type { NextRouter } from 'next/router';

import type { AppData } from '../../types/app_data';
import { AppStepSection, StepCategory } from '../../types/app_data';
import type { Step } from '../types/step';
import { RelationToUser } from '../../types/driver';
import { isEligibleForGeicoQuotes, isEligibleForMercuryQuotes } from '../../utils/onboard';
import type { SearchPolicyDriver } from '../../types/policy';
import { isRated } from '../../utils/driver';

const canAskOtherDriverEducation = (appData: AppData, driver: SearchPolicyDriver) => {
  if (appData.stepSection === AppStepSection.Challenge) {
    if (driver?.education) {
      return false;
    }
  }

  return isEligibleForMercuryQuotes(appData);
};

const canAskUserDriverEducation = (appData: AppData) => {
  const state = appData?.address?.state || '';

  if (['MA'].includes(state)) {
    return false;
  }

  if (appData.signupParams?.needNNOInsurance === true) {
    return false;
  }

  if (appData.stepSection === AppStepSection.Challenge) {
    // Skip the step is it's already answered
    const userDriver = find(appData.drivers, (driver) => driver.relationToUser === RelationToUser.Insured);
    if (userDriver?.education) {
      return false;
    }
  }

  // If user is signup from partner link, and has prefilled with drivers and cars, skip loading & LN pulling
  if (appData.signupParams?.partnerSignup && !!appData.partnerSkippableData?.education) {
    return false;
  }

  return isEligibleForGeicoQuotes(appData);
};

const getDriverListForEducation = (appData: AppData): Array<{ driver: SearchPolicyDriver; eligible: boolean }> => {
  const list = orderBy(
    map(appData.drivers, (driver) => {
      if (driver.isArchived) {
        return { driver, eligible: false };
      }

      if (!isRated(driver) && driver.relationToUser !== RelationToUser.Spouse) {
        return { driver, eligible: false };
      }

      if (canAskOtherDriverEducation(appData, driver)) {
        return { driver, eligible: true };
      }

      if (driver.relationToUser === RelationToUser.Insured) {
        return { driver, eligible: canAskUserDriverEducation(appData) };
      }

      return { driver, eligible: false };
    }),
    [(item) => (item.driver.relationToUser === RelationToUser.Insured ? 0 : 1), (item) => item.driver.id],
    ['asc', 'asc']
  );

  return list;
};

export const getCurrentEducationDriver = (appData: AppData) => {
  const drivers = filter(getDriverListForEducation(appData), (item) => item.eligible).map((item) => item.driver);
  return drivers.find((driver) => driver.id === appData.currentEducationDriver?.id) || drivers[0];
};

export const getNextEducationDriver = (appData: AppData, currentID: string | undefined) => {
  const list = getDriverListForEducation(appData);
  const currentDriver = getCurrentEducationDriver(appData);
  const currentIndex = list.findIndex((item) => item.driver.id === (currentID || currentDriver?.id));

  if (currentIndex < 0) {
    return;
  }

  return list.find((item, index) => index > currentIndex && item.eligible)?.driver;
};

// Ask driver for occupation
export const EducationStep: Step = {
  id: 'education',
  pathname: '/education',

  canShow: (appData: AppData) => {
    const driver = getCurrentEducationDriver(appData);
    return !!driver?.id;
  },
  hasData: () => true,
  hasOwnData: () => true,
  queryParams: (appData: AppData, router: NextRouter) => {
    return {
      eduDrvId: getCurrentEducationDriver(appData)?.id || router.query.eduDrvId || null,
    };
  },
  category: () => StepCategory.Drivers,
  specialNextStep: {
    nextStepId: 'education',
    enable: (appData: AppData) => {
      return !!appData?.currentEducationDriver?.id;
    },
  },
};
